/**
 * ImageGeneratorForm - Form fields for generating images with AI
 * Always visible at the top of the panel (no collapsible toggle).
 */

import React, { RefObject } from "react";

import {
  RefreshCw,
  Wand2,
  X,
  ImagePlus,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  ASPECT_RATIOS,
  STYLE_PRESETS,
  MAX_REFERENCE_IMAGES,
  ImageGenRequestItem,
  ReferenceImage,
} from "@/types/image-generate-panel.types";

interface ImageGeneratorFormProps {
  // Form state
  prompt: string;
  onPromptChange: (value: string) => void;
  filename: string;
  onFilenameChange: (value: string) => void;
  aspectRatio: string;
  onAspectRatioChange: (value: string) => void;
  stylePreset: string;
  onStylePresetChange: (value: string) => void;

  // Reference images
  referenceImages: ReferenceImage[];
  fileInputRef: RefObject<HTMLInputElement>;
  onReferenceImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveReferenceImage: (index: number) => void;

  // Selected images for editing
  selectedImagesForEdit: ImageGenRequestItem[];
  onClearSelection: () => void;
  onToggleSelection: (id: string) => void;

  // Generation
  isGenerating: boolean;
  onGenerate: () => void;
}

export function ImageGeneratorForm({
  prompt,
  onPromptChange,
  filename,
  onFilenameChange,
  aspectRatio,
  onAspectRatioChange,
  stylePreset,
  onStylePresetChange,
  referenceImages,
  fileInputRef,
  onReferenceImageUpload,
  onRemoveReferenceImage,
  selectedImagesForEdit,
  onClearSelection,
  onToggleSelection,
  isGenerating,
  onGenerate,
}: ImageGeneratorFormProps): React.ReactElement {
  return (
    <div className="space-y-3">
      {/* Selected images for editing */}
      {selectedImagesForEdit.length > 0 && (
        <div className="p-1.5 rounded-md bg-purple-500/10 border border-purple-500/20 flex items-center gap-2">
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            {selectedImagesForEdit.slice(0, 3).map((img) => (
              <div key={img.id} className="relative inline-block flex-shrink-0">
                <img
                  src={img.downloadUrl}
                  alt={img.expectedFilename}
                  className="h-8 w-8 rounded object-cover border border-purple-500/30"
                />
                <button
                  onClick={() => onToggleSelection(img.id)}
                  className="absolute -top-1 -right-1 p-0.5 rounded-full bg-background border border-border shadow-sm hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                  title="Remove from selection"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
            {selectedImagesForEdit.length > 3 && (
              <span className="text-[10px] font-medium text-purple-600 dark:text-purple-400 flex-shrink-0">
                +{selectedImagesForEdit.length - 3}
              </span>
            )}
            <span className="text-[10px] text-purple-600 dark:text-purple-400 truncate">
              {selectedImagesForEdit.length === 1
                ? selectedImagesForEdit[0].expectedFilename
                : `${selectedImagesForEdit.length} selected`}
            </span>
          </div>
          <button
            onClick={onClearSelection}
            className="p-0.5 rounded hover:bg-purple-500/20 text-purple-500 transition-colors flex-shrink-0"
            title="Clear selection"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Prompt Input */}
      <div className="relative">
        <Textarea
          placeholder={
            selectedImagesForEdit.length > 1
              ? "Describe how to combine these images..."
              : selectedImagesForEdit.length === 1
                ? "Describe how you want to edit this image..."
                : "Describe the image you want to create..."
          }
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          className={cn(
            "min-h-[56px] max-h-[100px] resize-none text-sm pr-16",
            "bg-background border-border/50",
            "focus:border-border focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0",
            "placeholder:text-muted-foreground/50",
            "transition-all duration-200"
          )}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              onGenerate();
            }
          }}
        />
        {/* Action buttons */}
        <div className="absolute top-2 right-2 flex items-center gap-1">
          {referenceImages.length < MAX_REFERENCE_IMAGES && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-1 rounded hover:bg-muted text-muted-foreground/60 hover:text-foreground transition-colors"
              title={`Add reference image (${referenceImages.length}/${MAX_REFERENCE_IMAGES})`}
            >
              <ImagePlus className="w-3.5 h-3.5" />
            </button>
          )}
          {prompt && (
            <button
              onClick={() => onPromptChange("")}
              className="p-1 rounded hover:bg-muted text-muted-foreground/60 hover:text-foreground transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={onReferenceImageUpload}
          className="hidden"
        />
      </div>

      {/* Reference images preview */}
      {referenceImages.length > 0 && (
        <div className="flex items-center gap-1.5">
          {referenceImages.map((img, index) => (
            <div key={index} className="relative inline-block">
              <img
                src={img.preview}
                alt={`Reference ${index + 1}`}
                className="h-10 w-10 rounded border border-border/50 object-cover"
              />
              <button
                onClick={() => onRemoveReferenceImage(index)}
                className="absolute -top-1 -right-1 p-0.5 rounded-full bg-background border border-border shadow-sm hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
          {referenceImages.length < MAX_REFERENCE_IMAGES && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="h-10 w-10 rounded border border-dashed border-border/50 flex items-center justify-center hover:bg-muted/50 hover:border-border text-muted-foreground/50 hover:text-muted-foreground transition-colors"
              title="Add more reference images"
            >
              <ImagePlus className="w-3.5 h-3.5" />
            </button>
          )}
          <span className="text-[10px] text-muted-foreground/60">
            {referenceImages.length}/{MAX_REFERENCE_IMAGES}
          </span>
        </div>
      )}

      {/* Options Row: Aspect Ratio + Style + Filename */}
      <div className="flex items-center gap-1.5">
        <Select
          value={aspectRatio}
          onValueChange={onAspectRatioChange}
          defaultValue={ASPECT_RATIOS[0].value}
        >
          <SelectTrigger className="h-7 text-xs w-[100px] bg-background border-border/50">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ASPECT_RATIOS.map((ratio) => (
              <SelectItem
                key={ratio.value}
                value={ratio.value}
                className="text-xs"
              >
                {ratio.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={stylePreset}
          onValueChange={onStylePresetChange}
          defaultValue={STYLE_PRESETS[0].value}
        >
          <SelectTrigger className="h-7 text-xs w-[90px] bg-background border-border/50">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STYLE_PRESETS.map((style) => (
              <SelectItem
                key={style.value}
                value={style.value}
                className="text-xs"
              >
                {style.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          placeholder="Filename (auto)"
          value={filename}
          onChange={(e) => onFilenameChange(e.target.value)}
          className={cn(
            "h-7 text-xs flex-1",
            "bg-background border-border/50",
            "focus:border-border focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0",
            "placeholder:text-muted-foreground/40"
          )}
        />
      </div>

      {/* Generate Button */}
      <Button
        onClick={onGenerate}
        disabled={isGenerating || !prompt.trim()}
        className={cn(
          "w-full h-9 text-xs font-medium rounded-lg",
          "bg-purple-600 hover:bg-purple-700",
          "text-white",
          "transition-all duration-200",
          "disabled:opacity-50 disabled:cursor-not-allowed"
        )}
      >
        {isGenerating ? (
          <>
            <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            {selectedImagesForEdit.length > 1
              ? "Composing..."
              : selectedImagesForEdit.length === 1
                ? "Editing..."
                : "Starting..."}
          </>
        ) : (
          <>
            <Wand2 className="w-3.5 h-3.5 mr-1.5" />
            {selectedImagesForEdit.length > 1
              ? "Compose"
              : selectedImagesForEdit.length === 1
                ? "Edit Image"
                : "Generate"}
          </>
        )}
      </Button>
    </div>
  );
}
